import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { useDebounceCallback } from "utils/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import Loading from "../Loading";

interface DropdownOption {
  value: string;
  label: string | JSX.Element;
  stringSearch?: string;
}

interface SearchableDropdownProps {
  options: DropdownOption[];
  selectedValue: string | null | JSX.Element;
  onSelect: (value: string) => void;
  className?: string;
  searchPlaceholder?: string;
  emptyDropdownState?: JSX.Element;
  disabled?: boolean;
}

const SearchableDropdownAlt: React.FC<SearchableDropdownProps> = ({
  options,
  searchPlaceholder,
  onSelect,
  className,
  selectedValue,
  emptyDropdownState,
  disabled,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [trueSearchTerm, setTrueSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] =
    useState<DropdownOption[]>(options);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  //-----------------------------------Callback hooks---------------------------------------------

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchTerm(value);
    debounceSearch(value);
  };

  const debounceSearch = useDebounceCallback((value: string) => {
    const filtered = options.filter((option) => {
      let optionString =
        typeof option.label === "string" ? option.label : option.value;
      if (option.stringSearch) {
        optionString = option.stringSearch;
      }
      return optionString.toLowerCase().includes(value.toLowerCase());
    });
    setTrueSearchTerm(value);
    setFilteredOptions(filtered);
  }, 300);

  const handleToggle = (isOpen: boolean) => {
    setShowDropdown(isOpen);
  };

  const getDropdownClassName = () => {
    let dropdownClassName = "searchable-dropdown";

    if (className) {
      dropdownClassName += ` ${dropdownClassName}`;
    }
    return dropdownClassName;
  };

  const getSearchableInput = () => {
    return (
      <div
        className="search-input-container"
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          margin: "8px",
        }}
      >
        <div
          style={{ position: "absolute", left: "8px", pointerEvents: "none" }}
        >
          <FontAwesomeIcon
            icon={faSearch}
            style={{ color: "#001F4E", fontSize: "12px" }}
          />
        </div>
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearch}
          placeholder={searchPlaceholder}
          style={{
            paddingLeft: "24px",
            width: "100%",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />
      </div>
    );
  };

  const getDropdownOptions = () => {
    if (trueSearchTerm !== searchTerm) {
      return <Loading />;
    }
    if (!filteredOptions?.length) {
      return (
        emptyDropdownState ?? <Dropdown.Item disabled>No results</Dropdown.Item>
      );
    }
    return filteredOptions.map((option, index) => (
      <React.Fragment key={option.value}>
        <Dropdown.Item onClick={() => onSelect(option.value)}>
          {option.label}
        </Dropdown.Item>
        {index !== filteredOptions.length - 1 && <Dropdown.Divider />}
      </React.Fragment>
    ));
  };

  return (
    <Dropdown
      show={showDropdown}
      className={getDropdownClassName()}
      onToggle={handleToggle}
    >
      <Dropdown.Toggle
        variant="light"
        id="dropdown-basic"
        className="row-gap-16px align-items-center"
        disabled={disabled}
      >
        {selectedValue}
      </Dropdown.Toggle>
      <Dropdown.Menu className="searchable-dropdown-menu">
        {getSearchableInput()}
        <Dropdown.Divider />
        {getDropdownOptions()}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default SearchableDropdownAlt;
